import { applyChatEvent, readChatSources, type ChatConversation, type ChatTurn } from "./chat-model";

const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
const date = (value: unknown) =>
  typeof value === "string" ? Date.parse(value) : typeof value === "number" ? value : NaN;
// The original writer allowed absent source titles. Identity and citation text
// still belong to the transcript even when its preview title was unavailable.
const sources = (value: unknown) =>
  Array.isArray(value)
    ? value.map(entry => {
        const source = record(entry);
        return { ...source, title: typeof source.title === "string" ? source.title : "" };
      })
    : [];

function readTurn(value: unknown): ChatTurn {
  const old = record(value),
    createdAt = date(old.createdAt);
  if (
    typeof old.id !== "string" ||
    !old.id ||
    typeof old.query !== "string" ||
    !Number.isFinite(createdAt) ||
    !["loading", "done", "error"].includes(String(old.status))
  )
    throw new Error("Unreadable legacy turn");
  let turn: ChatTurn = {
    id: old.id,
    query: old.query,
    createdAt,
    state: "interrupted",
    text: typeof old.streamingText === "string" ? old.streamingText : "",
    sources: readChatSources(sources(old.sources)),
    images: [],
  };
  if (old.response !== undefined) {
    const response = record(old.response),
      answer = record(response.answer);
    const completed = applyChatEvent(turn, {
      type: "complete",
      data: {
        ...response,
        answer: { ...answer, cards: sources(answer.cards) },
        ...(Array.isArray(response.sources) ? { sources: sources(response.sources) } : {}),
      },
    });
    // Import a transcript, never a recoverable request or executable receipt.
    turn = {
      ...turn,
      text: completed.text,
      answer: completed.answer,
      sources: completed.sources,
      images: completed.images,
      textSettled: completed.textSettled,
      state: completed.state,
    };
  }
  if (old.status !== "done") turn.state = old.status === "error" ? "failed" : "interrupted";
  if (typeof old.error === "string") turn.error = old.error;
  return turn;
}

/** Decode the account-keyed /stasht-chat writer, without changing its source.
 * An invalid thread stays wholly in the old store, rather than marking a
 * silently shortened transcript imported. Intact sibling threads can proceed. */
export function readLegacyChats(raw: string, importedIds: string[]) {
  let entries: unknown;
  try {
    entries = JSON.parse(raw);
  } catch {
    return { conversations: [], incomplete: true };
  }
  if (!Array.isArray(entries)) return { conversations: [], incomplete: true };
  const imported = new Set(importedIds),
    seen = new Set<string>();
  const conversations: { sourceId: string; conversation: ChatConversation }[] = [];
  let incomplete = false;
  for (const value of entries) {
    const old = record(value);
    if (typeof old.id === "string" && imported.has(old.id)) continue;
    try {
      const createdAt = date(old.createdAt),
        updatedAt = date(old.updatedAt);
      if (
        typeof old.id !== "string" ||
        !old.id ||
        seen.has(old.id) ||
        typeof old.title !== "string" ||
        !Number.isFinite(createdAt) ||
        !Number.isFinite(updatedAt) ||
        !Array.isArray(old.turns)
      )
        throw new Error("Unreadable legacy thread");
      seen.add(old.id);
      const turns = old.turns.map(readTurn);
      if (new Set(turns.map(turn => turn.id)).size !== turns.length) throw new Error("Duplicate legacy turn");
      conversations.push({
        sourceId: old.id,
        conversation: { id: `legacy:${old.id}`, title: old.title, createdAt, updatedAt, draft: "", turns },
      });
    } catch {
      incomplete = true;
    }
  }
  return { conversations, incomplete };
}
